/**
 * Map Dynamic Scheduling compute_result → generic RunArtifact list.
 * A plan artifact is the assignment schedule; scorecard is a trailer line only.
 */

import type { AiStudioRun } from '@/types/aiStudioAgent';
import type { RunArtifact } from './artifacts';

function clock(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '?';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function nameById(
  rows: unknown[],
  id: string
): string {
  for (const raw of rows) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    if (String(r.id ?? '') === id) {
      const name = r.name;
      return typeof name === 'string' && name.trim() ? name.trim() : id;
    }
  }
  return id;
}

export function buildSchedulingArtifacts(run: AiStudioRun): RunArtifact[] {
  const cr = run.computeResult;
  if (!cr || typeof cr !== 'object') return [];
  const candidates = Array.isArray(cr.candidates) ? (cr.candidates as unknown[]) : [];
  if (!candidates.length) return [];

  const ws = (run.workingSet ?? {}) as Record<string, unknown>;
  const resources = Array.isArray(ws.resources) ? (ws.resources as unknown[]) : [];
  const demands = Array.isArray(ws.demands) ? (ws.demands as unknown[]) : [];
  const tasks = Array.isArray(ws.tasks) ? (ws.tasks as unknown[]) : [];
  const selectedId =
    typeof run.currentThesis?.selectedOptionId === 'string'
      ? run.currentThesis.selectedOptionId
      : null;

  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const artifacts: RunArtifact[] = [];

  candidates.forEach((raw, idx) => {
    if (!raw || typeof raw !== 'object') return;
    const c = raw as Record<string, unknown>;
    const optionId = typeof c.optionId === 'string' ? c.optionId : `opt_${idx}`;
    const label = typeof c.label === 'string' ? c.label : optionId;
    const letter = letters[idx] ?? String(idx + 1);
    const assignments = Array.isArray(c.assignments) ? (c.assignments as unknown[]) : [];
    const scores =
      c.scores && typeof c.scores === 'object' && !Array.isArray(c.scores)
        ? (c.scores as Record<string, unknown>)
        : {};

    const sorted = [...assignments].sort((a, b) => {
      if (!a || typeof a !== 'object' || !b || typeof b !== 'object') return 0;
      return (
        Number((a as Record<string, unknown>).startMinutes ?? 0) -
        Number((b as Record<string, unknown>).startMinutes ?? 0)
      );
    });

    const rows = sorted.map((item) => {
      const a =
        item && typeof item === 'object' ? (item as Record<string, unknown>) : {};
      const start = Number(a.startMinutes ?? 0);
      const end = Number(a.endMinutes ?? 0);
      const resourceId = String(a.resourceId ?? '?');
      const taskId = String(a.taskId ?? '?');
      const demandId = String(a.demandId ?? '?');
      const machine = nameById(resources, resourceId);
      const job = nameById(tasks, taskId);
      const order = nameById(demands, demandId);
      const dur = Number.isFinite(end - start) ? `${end - start} min` : '?';
      return {
        when: `${clock(start)}–${clock(end)}`,
        machine: `${machine} (${resourceId})`,
        job: `${job} / ${order}`,
        duration: dur,
      };
    });

    const onTime = Number(scores.onTimeRatio);
    const late = Number(scores.lateDemandCount);
    const util = Number(scores.utilization);
    const scoreLine = `On-time ${
      Number.isFinite(onTime) ? `${Math.round(onTime * 100)}%` : 'n/a'
    } · Late orders ${Number.isFinite(late) ? late : 'n/a'} · Utilization ${
      Number.isFinite(util) ? `${Math.round(util * 100)}%` : 'n/a'
    }`;

    const notes = Array.isArray(c.notes)
      ? (c.notes as unknown[]).filter((n): n is string => typeof n === 'string')
      : [];

    const selected = selectedId === optionId;
    artifacts.push({
      id: optionId,
      title: `Plan ${letter}${selected ? ' (selected)' : ''}`,
      summary: `${label} · ${rows.length} assignments`,
      kind: 'schedule',
      body: {
        type: 'schedule',
        objective: label,
        scoreLine,
        rows,
        notes,
      },
    });
  });

  return artifacts;
}
